import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';

import { Helmet } from 'react-helmet-async';

import SpinnerComponent from '../Common/Spinners';
import LeftSidebar from '../Common/LeftSidebar';
import StatsFilters from '../StatsTable/Filters';
import StatsHeaderCell from '../StatsTable/Header.js';
import StatsRow from '../StatsTable/Row.js';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faBullseye } from '@fortawesome/free-solid-svg-icons';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  font-size: .85em;
  min-width: 20em;
  max-width: 75em;

  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
    font-size: .85em;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2em;
  min-height: 38em;
  
  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
  }
`;

const PageTitle = styled.div`
  font-size: 2em;
  font-weight: bold;
`;

const PlayerTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1em;
`

const PlayerTableContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid black;
`

const PlayerRankingsHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: white;
  position: -webkit-sticky;
  position: sticky;
  /* top: 62px;
  z-index: 1; */
  overflow-x: hidden;
  min-height: 1.3em;
  margin-top: 0.5em;
  margin-right: ${(props) =>
    props.$safari === true
      ? '0'
      : '17px'}; // To sync with scrollbar width of table below

  @media (max-width: 768px) {
    overflow-y: scroll;
    margin-right: 0;
  }
`

const TableContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  min-width: 100%;
  overflow-y: scroll;
  max-height: ${(props) => (props.$safari === true ? 'none' : '35em')};

  @media (max-width: 768px) {
    max-height: 30em;
  }

  @supports (-webkit-overflow-scrolling: touch) {
    max-height: none;
  }
`

const TableContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

const NoDataContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 8em;
  padding: 2em 0;
  font-size: 1.2em;
`

const PlayerRankings = ({ AppStore }) => {
  const isSafari =
    navigator.userAgent.indexOf("Safari") > -1 &&
    navigator.userAgent.indexOf("Chrome") === -1;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  const route = 'playerRankings';
  const didRouteChange = route !== AppStore.currentRoute.get();
  const isFetching = AppStore.isStatsFetching.get();
  const rankingsYearsFetched = AppStore.playerRankingsFetched;
  const isSorting = AppStore.isStatsSorting.get();

  const statsDefinitionsAreFetched = AppStore.statsDefinitionsAreFetched.get();
  const statsDefinitionsAreFetching = AppStore.statsDefinitionsAreFetching.get();

  const yearToFetch = AppStore.statsFilters.year.year ? AppStore.statsFilters.year.year : new Date().getFullYear();

  const pageTitle = 'OE Valorant Player Rankings';
  const pageDescription = 'OE Valorant player rankings (WIP)';

  useEffect(() => {
    if (didRouteChange) {
      AppStore.setCurrentRoute(route);
      AppStore.setSortColumn('playerRankings', 'RAR');
    }

    if ((!rankingsYearsFetched.includes(yearToFetch) && !isFetching) || didRouteChange) {
      AppStore.fetchPlayerRankings(yearToFetch);
    }

    if (!statsDefinitionsAreFetched && !statsDefinitionsAreFetching) {
      AppStore.getStatsDefinitions();
    }
  }, [
    AppStore,
    didRouteChange,
    AppStore.currentRoute,
    rankingsYearsFetched,
    isFetching,
    yearToFetch,
    statsDefinitionsAreFetching,
    statsDefinitionsAreFetched
  ]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name={pageTitle}
          content={pageDescription}
        />
      </Helmet>
      <LeftSidebar />
      <Wrapper className="mainContent">
        <Content>
          <PageTitle>Top Players</PageTitle>
          {(AppStore.userInfoFetching.get() || (didRouteChange || Object.keys(rankingsYearsFetched).length < 1 || isFetching || isSorting)) ? (
            <SpinnerComponent />
          ) : (
            <>
              <ScrollSync>
                <PlayerTableContainer>
                  <StatsFilters view='playerRankings' />
                  <PlayerTableContentWrapper>
                    {AppStore.filteredStatsContents.length > 0 ? (
                      <>
                        <ScrollSyncPane>
                          <PlayerRankingsHeader $safari={isSafari}>
                            {AppStore.statsColumns.map(
                              (column, index) => {
                                return (
                                  <StatsHeaderCell
                                    key={column}
                                    index={index}
                                    label={column}
                                    view='playerRankings'
                                  />
                                );
                              }
                            )}
                          </PlayerRankingsHeader>
                        </ScrollSyncPane>
                        <ScrollSyncPane>
                          <TableContentWrapper $safari={isSafari}>
                            <TableContentContainer>
                              {
                                // TODO: Add checks for data availability / fetch status etc.
                                AppStore.filteredStatsContents.map((player, rowIndex) => {
                                  return (
                                    <StatsRow
                                      key={`${player.playerId}-row`}
                                      rowData={player}
                                      rowIndex={rowIndex}
                                      statsType='topPlayers'
                                    />
                                  )
                                })
                              }
                            </TableContentContainer>
                          </TableContentWrapper>
                        </ScrollSyncPane>
                      </>
                    ) : (
                      <TableContentWrapper>
                        <NoDataContainer>
                          No player rankings are available for these filters
                        </NoDataContainer>
                      </TableContentWrapper>
                    )}
                  </PlayerTableContentWrapper>
                  <div style={{'paddingTop': '0.5em', 'fontSize': '0.9em', 'fontStyle': 'italic'}}>Hover column headers for definitions.</div>
                </PlayerTableContainer>
              </ScrollSync>
            </>
          )}
        </Content>
      </Wrapper >
    </>
  );
};

export default inject("AppStore")(observer(PlayerRankings));
